/** Auth for one robot's video, from robot_hub `POST /video/token`. */
export interface VideoAuth {
  url: string;
  token: string;
  expires_at: number; // unix seconds
}

/** Fetches a fresh token for `robotId` (each app has its own way to reach robot_hub). */
export type GetVideoAuth = (robotId: string) => Promise<VideoAuth>;

/** The SFU track name of a camera stream: `{robot}_{camera}{suffix}`, as the robot publishes it. */
export function trackName(robotId: string, camera: string, suffix = ''): string {
  return `${robotId}_${camera}${suffix}`.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 200);
}

/** The track names the robot publishes now (empty when it is not publishing). */
export async function listTracks(getAuth: GetVideoAuth, robotId: string, signal?: AbortSignal): Promise<string[]> {
  const auth = await getAuth(robotId);
  return (await call<{ tracks: string[] }>(`${auth.url}/v1/tracks`, auth.token, 'GET', undefined, signal)).tracks;
}

/**
 * The track that shows `camera`: `{robot}_{camera}` for a mono camera, the
 * left eye `{robot}_{camera}-left` for a stereo ZED. Null when the robot does
 * not publish the camera.
 */
export function cameraTrack(tracks: string[], robotId: string, camera: string): string | null {
  const mono = trackName(robotId, camera);
  const left = trackName(robotId, camera, '-left');
  return tracks.includes(mono) ? mono : tracks.includes(left) ? left : null;
}

interface PullResponse {
  sessionId: string;
  sdp: string | null;
  tracks: { name: string; mid: string }[];
  iceServers: RTCIceServer[];
  leaseToken: string;
  leaseRenewMs: number;
}

class VideoHttpError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function call<T>(url: string, token: string, method: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(url, {
    method,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new VideoHttpError(resp.status, json.error ?? `HTTP ${resp.status}`);
  return json as T;
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(signal.reason);
    });
  });

// The robot starts sending a track when the first lease on it reaches it; until
// then the SFU answers 503 and the pull is retried.
const WARMUP_RETRY_MS = 1000;
const WARMUP_TIMEOUT_MS = 20_000;
const KEYFRAME_REPEAT_MS = 1000;

/**
 * One viewer session on the SFU, holding one or more tracks of one robot.
 *
 * `open` resolves once the SDP exchange is done; `streams` fill as tracks
 * arrive. `ended` resolves when the session is lost (connection failed, or the
 * robot published again so these tracks are gone); open a new one then.
 */
export class VideoPull {
  readonly streams = new Map<string, MediaStream>();
  readonly ended: Promise<string>;
  private endWith!: (reason: string) => void;
  private leaseTimer: ReturnType<typeof setInterval> | undefined;
  private closed = false;

  private readonly auth: VideoAuth;
  private readonly pc: RTCPeerConnection;
  private readonly leaseToken: string;
  private readonly onTrack: () => void;

  private constructor(auth: VideoAuth, pc: RTCPeerConnection, leaseToken: string, onTrack: () => void) {
    this.auth = auth;
    this.pc = pc;
    this.leaseToken = leaseToken;
    this.onTrack = onTrack;
    this.ended = new Promise((resolve) => (this.endWith = resolve));
  }

  static async open(
    getAuth: GetVideoAuth,
    robotId: string,
    tracks: string[],
    // jitterBuffer: false plays each frame as soon as it decodes; true keeps the
    // browser's adaptive jitter buffer (smoother, later).
    opts: { jitterBuffer: boolean; signal?: AbortSignal; onTrack?: () => void },
  ): Promise<VideoPull> {
    const { signal } = opts;
    let auth = await getAuth(robotId);
    const deadline = Date.now() + WARMUP_TIMEOUT_MS;
    let r: PullResponse;
    for (;;) {
      if (auth.expires_at * 1000 < Date.now() + 5000) auth = await getAuth(robotId);
      try {
        r = await call<PullResponse>(`${auth.url}/v1/pull`, auth.token, 'POST', { tracks }, signal);
        break;
      } catch (e) {
        if (!(e instanceof VideoHttpError && e.status === 503) || Date.now() > deadline) throw e;
        await sleep(WARMUP_RETRY_MS, signal);
      }
    }

    const pc = new RTCPeerConnection({ iceServers: r.iceServers, bundlePolicy: 'max-bundle' });
    const pull = new VideoPull(auth, pc, r.leaseToken, opts.onTrack ?? (() => {}));
    const byMid = new Map(r.tracks.map((t) => [t.mid, t.name]));
    pc.ontrack = (e) => {
      const name = byMid.get(e.transceiver.mid ?? '');
      if (!name) return;
      if (!opts.jitterBuffer) (e.receiver as RTCRtpReceiver & { jitterBufferTarget?: number }).jitterBufferTarget = 0;
      pull.streams.set(name, new MediaStream([e.track]));
      pull.onTrack();
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        // The robot sends a keyframe on request; the second covers one that left
        // before the SFU forwarded this session's tracks.
        void pull.requestKeyframe();
        setTimeout(() => void pull.requestKeyframe(), KEYFRAME_REPEAT_MS);
      }
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') pull.end(`connection ${pc.connectionState}`);
    };
    try {
      if (r.sdp) {
        await pc.setRemoteDescription({ type: 'offer', sdp: r.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await call(`${auth.url}/v1/pull/answer`, r.leaseToken, 'PUT', { sdp: answer.sdp }, signal);
      }
    } catch (e) {
      pull.close();
      throw e;
    }
    pull.leaseTimer = setInterval(() => void pull.renew(), r.leaseRenewMs);
    return pull;
  }

  private async requestKeyframe(): Promise<void> {
    if (this.closed) return;
    try {
      await call(`${this.auth.url}/v1/keyframe`, this.leaseToken, 'POST');
    } catch (e) {
      console.warn('robot-video: keyframe request failed', e);
    }
  }

  private async renew(): Promise<void> {
    try {
      const { stale } = await call<{ stale: boolean }>(`${this.auth.url}/v1/lease`, this.leaseToken, 'POST');
      if (stale) this.end('robot published again');
    } catch (e) {
      // One missed renewal is harmless: the lease outlives three intervals.
      console.warn('robot-video: lease renewal failed', e);
    }
  }

  private end(reason: string): void {
    if (this.closed) return;
    this.close();
    this.endWith(reason);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    clearInterval(this.leaseTimer);
    this.pc.close();
    // Lets the robot stop sending without waiting for the lease to expire.
    void fetch(`${this.auth.url}/v1/lease`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${this.leaseToken}` },
      keepalive: true,
    }).catch(() => {});
    this.endWith('closed');
  }
}
